import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from 'components/ProtectedRoute';
import PermissionRoute from 'components/PermissionRoute';
import { PERMISSIONS } from 'constants/permissions';

// render- Dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));

// render - categories
const CategoriesList = Loadable(lazy(() => import('pages/categories/list')));

// render - products
const ProductsList = Loadable(lazy(() => import('pages/products/list')));

// render - attributes
const AttributesList = Loadable(lazy(() => import('pages/attributes/list')));

// render - orders
const OrdersList = Loadable(lazy(() => import('pages/orders/list')));
const OrderDetail = Loadable(lazy(() => import('pages/orders/detail')));

// render - coupons
const CouponsList = Loadable(lazy(() => import('pages/coupons/list')));

// render - special sales
const SpecialSalesList = Loadable(lazy(() => import('pages/special-sales/list')));

// render - content
const ArticleCategoriesList = Loadable(lazy(() => import('pages/article-categories/list')));
const ArticlesList = Loadable(lazy(() => import('pages/articles/list')));
const ArticleForm = Loadable(lazy(() => import('pages/articles/form')));

// render - reviews
const ReviewsList = Loadable(lazy(() => import('pages/reviews/list')));

// render - profile pages
const ProfileView = Loadable(lazy(() => import('pages/profile/view')));
const ProfileEdit = Loadable(lazy(() => import('pages/profile/edit')));
const ChangePassword = Loadable(lazy(() => import('pages/profile/change-password')));

// render - error pages
const Error403 = Loadable(lazy(() => import('pages/error/403')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes: RouteObject = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    {
      path: '/',
      element: <DashboardDefault />
    },
    {
      path: 'dashboard',
      children: [
        {
          path: 'default',
          element: <DashboardDefault />
        }
      ]
    },
    {
      path: 'categories',
      children: [
        {
          path: '',
          element: <CategoriesList />
        },
        {
          path: 'list',
          element: <CategoriesList />
        }
      ]
    },
    {
      path: 'products',
      children: [
        {
          path: '',
          element: <ProductsList />
        },
        {
          path: 'list',
          element: <ProductsList />
        }
      ]
    },
    {
      path: 'attributes',
      children: [
        {
          path: '',
          element: <AttributesList />
        },
        {
          path: 'list',
          element: <AttributesList />
        }
      ]
    },
    {
      path: 'orders',
      children: [
        {
          path: '',
          element: (
            <PermissionRoute permission={PERMISSIONS.ORDERS_VIEW}>
              <OrdersList />
            </PermissionRoute>
          )
        },
        {
          path: 'list',
          element: (
            <PermissionRoute permission={PERMISSIONS.ORDERS_VIEW}>
              <OrdersList />
            </PermissionRoute>
          )
        },
        {
          path: ':id',
          element: (
            <PermissionRoute permission={PERMISSIONS.ORDERS_VIEW}>
              <OrderDetail />
            </PermissionRoute>
          )
        }
      ]
    },
    {
      path: 'coupons',
      children: [
        {
          path: '',
          element: (
            <PermissionRoute permission={PERMISSIONS.COUPONS_VIEW}>
              <CouponsList />
            </PermissionRoute>
          )
        },
        {
          path: 'list',
          element: (
            <PermissionRoute permission={PERMISSIONS.COUPONS_VIEW}>
              <CouponsList />
            </PermissionRoute>
          )
        }
      ]
    },
    {
      path: 'special-sales',
      element: (
        <PermissionRoute permission={PERMISSIONS.SPECIAL_SALES_VIEW}>
          <SpecialSalesList />
        </PermissionRoute>
      )
    },
    {
      path: 'article-categories',
      children: [
        {
          path: '',
          element: (
            <PermissionRoute permission={PERMISSIONS.ARTICLE_CATEGORIES_VIEW}>
              <ArticleCategoriesList />
            </PermissionRoute>
          )
        },
        {
          path: 'list',
          element: (
            <PermissionRoute permission={PERMISSIONS.ARTICLE_CATEGORIES_VIEW}>
              <ArticleCategoriesList />
            </PermissionRoute>
          )
        }
      ]
    },
    {
      path: 'articles',
      children: [
        {
          path: '',
          element: (
            <PermissionRoute permission={PERMISSIONS.ARTICLES_VIEW}>
              <ArticlesList />
            </PermissionRoute>
          )
        },
        {
          path: 'create',
          element: (
            <PermissionRoute permission={PERMISSIONS.ARTICLES_CREATE}>
              <ArticleForm />
            </PermissionRoute>
          )
        },
        {
          path: ':id/edit',
          element: (
            <PermissionRoute permission={PERMISSIONS.ARTICLES_EDIT}>
              <ArticleForm />
            </PermissionRoute>
          )
        }
      ]
    },
    {
      path: 'reviews',
      children: [
        {
          path: '',
          element: (
            <PermissionRoute permission={PERMISSIONS.REVIEWS_VIEW}>
              <ReviewsList />
            </PermissionRoute>
          )
        }
      ]
    },
    {
      path: 'profile',
      children: [
        {
          path: 'view',
          element: <ProfileView />
        },
        {
          path: 'edit',
          element: <ProfileEdit />
        },
        {
          path: 'change-password',
          element: <ChangePassword />
        }
      ]
    },
    {
      path: '403',
      element: <Error403 />
    }
  ]
};

export default MainRoutes;
